import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Form, Spinner, Table } from "react-bootstrap";
import {
  FaBullseye, FaStar, FaListUl, FaCheckCircle, FaHourglassHalf,
  FaEdit, FaSyncAlt, FaChevronDown, FaChevronRight, FaEye,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import api from "../../../api";
import { SOURCE_LINK_LABELS, kraWorkLink } from "../kraWork/kraWorkFormat";
import {
  CALCULATION_LABELS, DATA_SOURCE_META, EVIDENCE_STATUS_META, FEEDBACK_STATUS_META, STATUS_META, defaultMonthFor, fiscalYearLabelFor,
  formatFeedbackAverage, formatKpiValue, formatMonthKey, formatRatioParts, formatScore, monthKeyFor, monthsForFiscalYear,
} from "./performanceFormat";
import KpiDetailModal from "./KpiDetailModal";
import ManualEntryModal from "./ManualEntryModal";
import "../../tasks/PortalHomeCards.css";
import "./MyKraKpiPerformancePage.css";

// "My Performance" -- the logged-in user's KRA/KPI scorecard for one month of
// a fiscal year. Everything shown is computed server-side
// (GET /performance/my-kra-kpi, resolved from the session user); this page
// only formats it.

export function Pill({ meta }) {
  if (!meta) return null;
  return <span className={`kp-pill kp-pill-${meta.tone}`}>{meta.label}</span>;
}

function SummaryTile({ icon, tone, label, value, sub }) {
  return (
    <div className="tlp-kpi kp-tile">
      <span className={`tlp-kpi-icon tlp-kpi-icon-${tone}`}>{icon}</span>
      <div className="kp-tile-body">
        <span className="tlp-kpi-label">{label}</span>
        <span className="tlp-kpi-val">{value}</span>
        {sub && <span className="kp-tile-sub">{sub}</span>}
      </div>
    </div>
  );
}

function ProgressBar({ percent }) {
  const width = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <div className="kp-progress" role="progressbar" aria-valuenow={width} aria-valuemin={0} aria-valuemax={100}>
      <div className="kp-progress-fill" style={{ width: `${width}%` }} />
    </div>
  );
}

function KpiResult({ kpi, result }) {
  const parts = formatRatioParts(kpi.calculation_type, result);
  const empty = !result || result.value === null || result.value === undefined;
  return (
    <div className="kp-result">
      <span className={empty ? "kp-muted" : "kp-strong"}>{formatKpiValue(kpi.calculation_type, result)}</span>
      {parts && <span className="kp-sub">{parts}</span>}
    </div>
  );
}

// Phase 63: where the KPI's data comes from, as an action. Ticket KPIs open
// the existing ticket drill-down; KPIs with a "My KRA Work" source link to
// that tracker -- and when nothing was recorded for the month, say so and
// offer "Add Record" instead of a bare "No data".
function hasNoMonthData(kpi) {
  return !!kpi.work_source && kpi.work_source.kind !== "TICKETS" && kpi.data_source === "MANUAL"
    && (!kpi.current_month || kpi.current_month.value === null || kpi.current_month.value === undefined)
    && ["MANUAL_ENTRY_REQUIRED", "NO_DATA"].includes(kpi.status);
}

function SourceLinks({ kpi, month, onView }) {
  const source = kpi.work_source;
  if (!source) return null;
  if (source.kind === "TICKETS") {
    return (
      <div className="kp-source-links">
        <button type="button" className="kp-link" onClick={() => onView(kpi)}>View ERP Tickets</button>
      </div>
    );
  }
  const recordType = source.record_types[0];
  return (
    <div className="kp-source-links">
      {hasNoMonthData(kpi) && <Link to={kraWorkLink({ recordType, month, add: true })}>+ Add Record</Link>}
      <Link to={kraWorkLink({ recordType, month })}>{SOURCE_LINK_LABELS[recordType] || "Open KRA Work"}</Link>
    </div>
  );
}

// Phase 65: User Feedback line under a KPI's YTD result (only KPIs with a
// mapped feedback form). Every number is the API's; a month still waiting
// for responses says so instead of showing 0.
function FeedbackSummary({ kpi, onView }) {
  const fb = kpi.feedback;
  if (!fb) return null;
  const average = formatFeedbackAverage(fb.ytd.average, fb.max_score);
  return (
    <div className="kp-feedback-line">
      <span className="kp-sub">Feedback {average || "—"} · Responses {fb.ytd.responses}</span>
      {fb.month.status !== "RECEIVED" && <Pill meta={FEEDBACK_STATUS_META[fb.month.status]} />}
      <button type="button" className="kp-link kp-sub" onClick={() => onView(kpi)}>View feedback details</button>
    </div>
  );
}

function KpiTable({ kpis, month, onView, onEnter }) {
  return (
    <div className="table-responsive kp-table-wrap">
      <Table className="tlp-table kp-table kp-kpi-table mb-0" size="sm">
        {/* Same column widths in every KRA section, so the columns line up down the page. */}
        <colgroup>
          <col className="kp-col-kpi" /><col className="kp-col-weight" /><col className="kp-col-target" />
          <col className="kp-col-ytd" /><col className="kp-col-rating" /><col className="kp-col-weighted" />
          <col className="kp-col-status" /><col className="kp-col-source" /><col className="kp-col-actions" />
        </colgroup>
        <thead>
          <tr>
            <th>KPI</th>
            <th className="kp-c">Weight</th>
            <th>Annual Target</th>
            <th>YTD Actual</th>
            <th className="kp-c">Rating</th>
            <th className="kp-c">Weighted</th>
            <th>Status</th>
            <th className="kp-c">Source</th>
            <th className="kp-actions-head">Actions</th>
          </tr>
        </thead>
        <tbody>
          {kpis.map((kpi) => (
            <tr key={kpi.kpi_id}>
              <td data-label="KPI" className="kp-kpi-name">
                <button type="button" className="kp-link" onClick={() => onView(kpi)}>{kpi.kpi_name}</button>
                <span className="kp-sub">{CALCULATION_LABELS[kpi.calculation_type] || kpi.calculation_type}</span>
              </td>
              <td data-label="Weight" className="kp-c">{formatScore(kpi.weightage)}%</td>
              <td data-label="Annual Target" className="kp-target">{kpi.annual_target_label || formatScore(kpi.annual_target_value)}</td>
              <td data-label="YTD Actual">
                <KpiResult kpi={kpi} result={kpi.ytd} />
                {/* Not for a reviewer-rated count (no rating rule): its direction is
                    unknown (fewer audit observations is better), and the Annual
                    Target column already states the source target, e.g. "<=2". */}
                {kpi.calculation_type === "COUNT_CUMULATIVE" && kpi.target_to_date !== null && kpi.has_rating_rule && (
                  <span className="kp-sub">Target to date {formatScore(kpi.target_to_date)}</span>
                )}
                {kpi.feedback && kpi.annual_target_value !== null && (
                  <span className="kp-sub">Sessions YTD {formatScore(kpi.ytd.value)} / {formatScore(kpi.annual_target_value)}</span>
                )}
                <FeedbackSummary kpi={kpi} onView={onView} />
              </td>
              <td data-label="Rating" className="kp-c">
                {kpi.rating !== null
                  ? <span className="kp-rating">{kpi.rating}<small>/5</small></span>
                  : <span className="kp-muted">{kpi.has_rating_rule ? "—" : "Manual rating required"}</span>}
                {/* Phase 67: a judgement KPI rated by the reviewer, not by thresholds. */}
                {kpi.rating_source === "MANUAL_REVIEW" && (
                  <span className="kp-sub" data-testid={`kp-manual-rating-${kpi.kpi_id}`}>Reviewer rating</span>
                )}
              </td>
              <td data-label="Weighted" className="kp-c">{kpi.weighted_contribution !== null ? formatScore(kpi.weighted_contribution) : <span className="kp-muted">—</span>}</td>
              <td data-label="Status">
                <Pill meta={STATUS_META[kpi.status]} />
                {kpi.evidence_status && kpi.evidence_status !== "NOT_SUBMITTED" && (
                  <span className="kp-sub kp-evidence-sub">Evidence: {EVIDENCE_STATUS_META[kpi.evidence_status]?.label || kpi.evidence_status}</span>
                )}
                {hasNoMonthData(kpi) && <span className="kp-nodata">No data recorded for this period</span>}
              </td>
              <td data-label="Source" className="kp-c"><Pill meta={DATA_SOURCE_META[kpi.data_source]} /></td>
              <td data-label="Action" className="kp-actions">
                <div className="kp-action-btns">
                  {kpi.can_enter_data && (
                    <button type="button" className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={() => onEnter(kpi)}>
                      <FaEdit /> Enter Monthly Data
                    </button>
                  )}
                  <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm kp-icon-btn" onClick={() => onView(kpi)}
                    aria-label={`View details of ${kpi.kpi_name}`} title="View details">
                    <FaEye />{!kpi.can_enter_data && <span>View Details</span>}
                  </button>
                </div>
                <SourceLinks kpi={kpi} month={month} onView={onView} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}

function KraCard({ kra, month, expanded, onToggle, onView, onEnter }) {
  return (
    <div className="tlp-card kp-kra">
      <button type="button" className="kp-kra-head" onClick={onToggle} aria-expanded={expanded}>
        <span className="kp-kra-chevron">{expanded ? <FaChevronDown /> : <FaChevronRight />}</span>
        <div className="kp-kra-title">
          <strong>{kra.kra_name}</strong>
          <span className="kp-sub">
            {kra.kpi_count} KPI{kra.kpi_count === 1 ? "" : "s"} · {kra.on_track_count} on track · {kra.at_risk_count} at risk
          </span>
        </div>
        <span className="kp-weight-badge">{formatScore(kra.weightage)}%</span>
        <div className="kp-kra-score">
          <span className="kp-kra-score-val">{formatScore(kra.score)}<small> / {formatScore(kra.max_score)}</small></span>
          <ProgressBar percent={kra.progress_percent} />
          <span className="kp-sub">Achieved {formatScore(kra.score)} of max {formatScore(kra.max_score)}</span>
        </div>
      </button>
      {expanded && <KpiTable kpis={kra.kpis} month={month} onView={onView} onEnter={onEnter} />}
    </div>
  );
}

const MyKraKpiPerformancePage = () => {
  const [fy, setFy] = useState(() => fiscalYearLabelFor());
  const [monthNum, setMonthNum] = useState(() => defaultMonthFor(fiscalYearLabelFor()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [detailKpi, setDetailKpi] = useState(null);
  const [entryKpi, setEntryKpi] = useState(null);

  const monthKey = monthKeyFor(fy, monthNum);
  const monthOptions = useMemo(() => monthsForFiscalYear(fy), [fy]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/performance/my-kra-kpi", { params: { month: monthKey } });
      setData(res.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || "Could not load your KRA/KPI performance.");
    } finally {
      setLoading(false);
    }
  }, [monthKey]);

  useEffect(() => { load(); }, [load]);

  const fiscalYears = useMemo(() => {
    const set = new Set([fiscalYearLabelFor(), ...(data?.fiscal_years || [])]);
    return Array.from(set).sort().reverse();
  }, [data]);

  const changeFy = (value) => {
    setFy(value);
    setMonthNum(defaultMonthFor(value));
  };

  const openMonth = (key) => {
    const [y, m] = key.split("-").map(Number);
    const start = m >= 4 ? y : y - 1;
    setFy(`${start}-${String((start + 1) % 100).padStart(2, "0")}`);
    setMonthNum(m);
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const summary = data?.summary;
  const kras = data?.kras || [];
  const hasConfig = !!data?.configuration;

  return (
    <div className="tlp-page kp-page">
      <div className="tlp-header">
        <div>
          <div className="tlp-eyebrow">KRA / KPI</div>
          <h1 className="tlp-title">My Performance</h1>
          <p className="tlp-subtitle">
            {hasConfig ? `${data.configuration.name} · ` : ""}Scores as of {formatMonthKey(monthKey)} (financial year to date)
          </p>
        </div>
        <div className="tlp-hactions kp-filters">
          <Form.Group controlId="kp-fy">
            <Form.Label className="kp-filter-label">Financial Year</Form.Label>
            <Form.Select size="sm" value={fy} onChange={(e) => changeFy(e.target.value)}>
              {fiscalYears.map((label) => <option key={label} value={label}>FY {label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="kp-month">
            <Form.Label className="kp-filter-label">Month</Form.Label>
            <Form.Select size="sm" value={monthNum} onChange={(e) => setMonthNum(Number(e.target.value))}>
              {monthOptions.map((m) => (
                <option key={m.num} value={m.num} disabled={m.isFuture}>{m.label}</option>
              ))}
            </Form.Select>
          </Form.Group>
          <button type="button" className="tlp-btn tlp-btn-ghost kp-refresh" onClick={load} disabled={loading} aria-label="Refresh">
            <FaSyncAlt className={loading ? "kp-spin" : ""} />
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger mb-0">{error}</div>}

      {loading && !data ? (
        <div className="tlp-card kp-center"><Spinner animation="border" size="sm" /> <span>Loading your performance…</span></div>
      ) : !hasConfig && !error ? (
        <div className="tlp-card kp-center kp-empty">
          <strong>No KRA/KPI configuration assigned for {formatMonthKey(monthKey)}.</strong>
          <span>Ask your administrator to assign one, or pick another month.</span>
        </div>
      ) : hasConfig ? (
        <>
          <div className="tlp-kpi-strip kp-strip">
            <SummaryTile icon={<FaBullseye />} tone="purple" label="Overall Score"
              value={summary.overall_score === null ? "—" : <>{formatScore(summary.overall_score)}<small className="kp-of"> / 100</small></>}
              sub={`${formatScore(summary.evaluated_weightage)}% of weight rated`} />
            <SummaryTile icon={<FaStar />} tone="amber" label="Overall Rating"
              value={summary.overall_rating === null ? "—" : <>{formatScore(summary.overall_rating)}<small className="kp-of"> / 5</small></>} />
            <SummaryTile icon={<FaListUl />} tone="info" label="KPI Progress"
              value={<>{summary.kpis_with_data}<small className="kp-of"> / {summary.total_kpis}</small></>}
              sub="KPIs with data this year" />
            <SummaryTile icon={<FaCheckCircle />} tone="green" label="On Track KPIs" value={summary.kpis_on_track}
              sub={summary.kpis_at_risk ? `${summary.kpis_at_risk} at risk` : null} />
            <SummaryTile icon={<FaHourglassHalf />} tone="blue" label="Awaiting Review" value={summary.kpis_awaiting_review}
              sub={summary.kpis_requiring_manual_entry ? `${summary.kpis_requiring_manual_entry} need entry` : null} />
          </div>

          {data.configuration_check?.issues?.length > 0 && (
            <div className="alert alert-warning mb-0 kp-config-warning">
              <strong>Configuration needs attention:</strong>
              <ul className="mb-0">{data.configuration_check.issues.map((i) => <li key={i}>{i}</li>)}</ul>
            </div>
          )}

          <p className="kp-note">
            Score = Σ (rating ÷ 5 × KPI weight). KPIs without data or without a rating rule add nothing and are never counted as zero.
          </p>

          <div className="kp-kras">
            {kras.map((kra) => (
              <KraCard
                key={kra.kra_id}
                kra={kra}
                month={monthKey}
                expanded={expanded[kra.kra_id] ?? true}
                onToggle={() => setExpanded((prev) => ({ ...prev, [kra.kra_id]: !(prev[kra.kra_id] ?? true) }))}
                onView={setDetailKpi}
                onEnter={setEntryKpi}
              />
            ))}
          </div>

          <div className="tlp-card">
            <div className="tlp-card-head">
              <strong>Monthly Performance History</strong>
              <span className="tlp-count">Financial year to date, as of each month end</span>
            </div>
            <div className="table-responsive kp-table-wrap">
              <Table className="tlp-table kp-table kp-history mb-0" size="sm" hover>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Overall Score</th>
                    <th>Overall Rating</th>
                    <th>KPIs Evaluated</th>
                    <th>Manual Entries</th>
                    <th>Auto Entries</th>
                    <th>Pending Entries</th>
                    <th aria-label="Open" />
                  </tr>
                </thead>
                <tbody>
                  {(data.history || []).map((h) => (
                    <tr key={h.period_month} className={h.period_month === monthKey ? "kp-row-active" : ""}>
                      <td data-label="Month">{formatMonthKey(h.period_month)}</td>
                      <td data-label="Overall Score">{h.overall_score === null ? "—" : `${formatScore(h.overall_score)} / 100`}</td>
                      <td data-label="Overall Rating">{h.overall_rating === null ? "—" : `${formatScore(h.overall_rating)} / 5`}</td>
                      <td data-label="KPIs Evaluated">{h.kpis_evaluated}</td>
                      <td data-label="Manual Entries">{h.manual_entries}</td>
                      <td data-label="Auto Entries">{h.auto_entries}</td>
                      <td data-label="Pending Entries">{h.pending_entries}</td>
                      <td data-label="Open" className="kp-actions">
                        <button type="button" className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => openMonth(h.period_month)}
                          disabled={h.period_month === monthKey}>
                          {h.period_month === monthKey ? "Viewing" : "Open"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </>
      ) : null}

      {detailKpi && (
        <KpiDetailModal
          kpiId={detailKpi.kpi_id}
          kpiName={detailKpi.kpi_name}
          month={monthKey}
          onClose={() => setDetailKpi(null)}
          onEnter={() => { setEntryKpi(detailKpi); setDetailKpi(null); }}
        />
      )}
      {entryKpi && (
        <ManualEntryModal
          kpi={entryKpi}
          month={monthKey}
          onClose={() => setEntryKpi(null)}
          onSaved={() => { setEntryKpi(null); load(); }}
        />
      )}
    </div>
  );
};

export default MyKraKpiPerformancePage;
